import React, { useEffect, useState, FormEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import ModalLayout from '../layouts/ModalLayout';
import Button from '../buttons/Button';
import Input from '../misc/Input';
import Labeled from '../misc/Labeled';
import DropdownSelect from '../forms/DropdownSelect';

import { Bank, CreateBankAccountModel } from '../../../types/bankAccount';
import { validateDutchIBAN, formatIBAN, isValidIBANForBank } from '../../../utils/validationUtils';

interface CreateBankAccountModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess: (bankData: CreateBankAccountModel) => void;
}

const bankOptions = [
  { label: 'Rabobank', value: Bank.RABOBANK },
  { label: 'ABN AMRO', value: Bank.ABN_AMRO },
  { label: 'ING', value: Bank.ING },
  { label: 'ASN Bank', value: Bank.ASN_BANK },
];

export default function CreateBankAccountModal({
  open,
  setOpen,
  onSuccess,
}: CreateBankAccountModalProps): JSX.Element {
  const [bankData, setBankData] = useState<Omit<CreateBankAccountModel, 'bank'> & { bank?: Bank }>({
    bank: undefined,
    iban: 'NL',
    accountName: '',
  });
  const [ibanError, setIbanError] = useState<string>('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationTimeout, setValidationTimeout] = useState<NodeJS.Timeout | null>(null);
  const { t } = useTranslation('', { keyPrefix: 'page.financial.accountEntry.createBankModal' });

  useEffect(() => {
    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
    };
  }, [validationTimeout]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();

    if (!bankData.bank) {
      toast.error(t('selectBank'));
      return;
    }

    if (!bankData.accountName.trim()) {
      toast.error(t('validation.accountNameRequired'));
      return;
    }

    const ibanValidation = validateDutchIBAN(bankData.iban, t);
    if (!ibanValidation.isValid) {
      setIbanError(ibanValidation.error || t('validation.ibanChecksum'));
      toast.error(ibanValidation.error || t('validation.ibanChecksum'));
      return;
    }

    if (!isValidIBANForBank(bankData.iban, bankData.bank)) {
      setIbanError(t('validation.bankMismatch'));
      toast.error(t('validation.bankMismatch'));
      return;
    }

    setIbanError('');

    onSuccess({
      accountName: bankData.accountName,
      iban: bankData.iban.replace(/\s/g, ''),
      bank: bankData.bank,
    });
  };

  const handleBankNumberChange = (newNumber: string) => {
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    setBankData((prev) => ({ ...prev, iban: newNumber }));

    if (ibanError) {
      setIbanError('');
    }

    const cleanNumber = newNumber.replace(/\s/g, '').toUpperCase();
    if (cleanNumber.length >= 4) {
      setIsValidating(true);

      const timeout = setTimeout(() => {
        const validation = validateDutchIBAN(cleanNumber, t);
        if (!validation.isValid) {
          setIbanError(validation.error || t('validation.ibanChecksum'));
        } else if (bankData.bank && !isValidIBANForBank(cleanNumber, bankData.bank)) {
          setIbanError(t('validation.bankMismatch'));
        } else {
          setIbanError('');
        }
        setIsValidating(false);
      }, 800);

      setValidationTimeout(timeout);
    } else {
      setIbanError('');
      setIsValidating(false);
    }
  };

  const handleBankNumberBlur = () => {
    const cleanNumber = bankData.iban.replace(/\s/g, '').toUpperCase();
    const formattedNumber = formatIBAN(cleanNumber);
    setBankData((prev) => ({ ...prev, iban: formattedNumber }));
  };

  const resetForm = () => {
    if (validationTimeout) {
      clearTimeout(validationTimeout);
      setValidationTimeout(null);
    }

    setBankData({
      bank: undefined,
      iban: 'NL',
      accountName: '',
    });
    setIbanError('');
    setIsValidating(false);
  };

  const handleModalClose = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    setOpen(isOpen);
  };

  return (
    <ModalLayout
      open={open}
      setOpen={handleModalClose}
      closeButton
      className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[700px]">
      <div>
        <h2 className="mb-4 text-lg font-semibold">{t('title')}</h2>
        <form className="flex flex-col gap-2" onSubmit={handleSubmit}>
          <Input
            id="account-name"
            label={t('accountName')}
            type="text"
            placeholder={t('accountName')}
            value={bankData.accountName}
            onChange={(ev) => setBankData((prev) => ({ ...prev, accountName: ev.target.value }))}
          />
          <Labeled label={t('bank')}>
            <DropdownSelect
              options={bankOptions}
              value={bankData.bank}
              setValue={(value) => setBankData((prev) => ({ ...prev, bank: value }))}
            />
          </Labeled>
          {bankData.bank && (
            <div>
              <Input
                id="account-number"
                label={t('accountNumber')}
                type="text"
                value={bankData.iban}
                onChange={(ev) => handleBankNumberChange(ev.target.value)}
                onBlur={handleBankNumberBlur}
                className={ibanError ? 'border-red-500 focus:border-red-500' : ''}
                placeholder="NL91 ABNA 0417 1643 00"
              />
              {ibanError && <p className="mt-1 text-sm text-red-600">{ibanError}</p>}
              {isValidating && <p className="mt-1 text-sm text-gray-500">Validating IBAN...</p>}
            </div>
          )}
          <Button
            variant="primary-dark"
            type="submit"
            className="mt-4 self-end"
            disabled={!bankData.accountName || !bankData.bank || !!ibanError || isValidating}>
            {isValidating ? 'Validating...' : t('createButton')}
          </Button>
        </form>
      </div>
    </ModalLayout>
  );
}
